/**
 * Standard-Base64-Encoder (`+/`, mit `=`-Padding) ohne `btoa`.
 *
 * Die Konvention verbietet `atob`/`btoa`; daher manuelles Alphabet.
 * Spiegel zur Decoder-Seite (`pinning-module/base64-to-bytes.ts`).
 *
 * URL-safe Base64 (`-_`) ist explizit nicht das Ziel — Crypto-Wire-Format
 * verwendet Standard-Base64 mit Padding (siehe `shared/wire/cancel-request.ts`).
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BITS_PER_BYTE = 8;
const BITS_PER_CHAR = 6;
const SEXTET_MASK = 0x3f;
const BYTE_MASK = 0xff;
const GROUP_INPUT_BYTES = 3;
const PAD_CHAR = '=';

/**
 * Kodiert beliebige Bytes als Standard-Base64-String mit Padding.
 *
 * @param bytes Eingabe-Bytes (z. B. SPKI-DER, AES-GCM-Chiffrat, IV).
 */
export function bytesToBase64(bytes: Uint8Array): string {
  if (bytes.byteLength === 0) {
    return '';
  }
  const chunks: string[] = [];
  for (let readIndex = 0; readIndex < bytes.byteLength; readIndex += GROUP_INPUT_BYTES) {
    chunks.push(encodeChunk(bytes, readIndex));
  }
  return chunks.join('');
}

function encodeChunk(bytes: Uint8Array, readIndex: number): string {
  const remaining = bytes.byteLength - readIndex;
  const byte0 = bytes[readIndex] ?? 0;
  const byte1 = remaining > 1 ? (bytes[readIndex + 1] ?? 0) : 0;
  const byte2 = remaining > 2 ? (bytes[readIndex + 2] ?? 0) : 0;
  const combined =
    ((byte0 & BYTE_MASK) << (BITS_PER_BYTE * 2)) |
    ((byte1 & BYTE_MASK) << BITS_PER_BYTE) |
    (byte2 & BYTE_MASK);
  const char0 = charAt((combined >> (BITS_PER_CHAR * 3)) & SEXTET_MASK);
  const char1 = charAt((combined >> (BITS_PER_CHAR * 2)) & SEXTET_MASK);
  const char2 = remaining > 1 ? charAt((combined >> BITS_PER_CHAR) & SEXTET_MASK) : PAD_CHAR;
  const char3 = remaining > 2 ? charAt(combined & SEXTET_MASK) : PAD_CHAR;
  return `${char0}${char1}${char2}${char3}`;
}

function charAt(sextet: number): string {
  const char = BASE64_ALPHABET[sextet];
  if (char === undefined) {
    throw new Error('INVALID_BASE64_SEXTET');
  }
  return char;
}
